<script lang="ts">
	import Button from "@components/common/Button.svelte";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import { ButtonVariant } from "@components/common/ButtonVariant.js";
	import Formatted from "@components/common/Formatted.svelte";
	import Form from "@components/form/Form.svelte";
	import Input from "@components/form/Input.svelte";
	import { Auth } from "@utils/Auth.svelte.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import { onMount } from "svelte";
	import { AuthenticationModalKind } from "@core/schema/AuthenticationModalKind.js";
	import { createReactiveBreakpoint } from "@core/utils/createReactiveBreakpoint.js";
	import { expectErrorMessage } from "@core/utils/expectErrorMessage.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import translations from "./ConfirmSignUp.translations.json" with { type: "json" };

	let {
		/* eslint-disable prefer-const */
		searchParams,
		/* eslint-enable prefer-const */
		email = $bindable(searchParams?.get("email") ?? undefined),
	}: { searchParams?: URLSearchParams; email?: string } = $props();
	const { tenant } = getGlobalTemplateProps();
	const translate = getTranslate(translations);
	const isDesktop = createReactiveBreakpoint(">md");
	const confirmSignUp = createPromiseWithStatus(Auth.confirmSignUp.bind(Auth, tenant.locale));

	const error = $derived(
		confirmSignUp.value.status === "error" &&
			expectErrorMessage(confirmSignUp.value.error, tenant.locale, {}, translate("errorWhileConfirming")),
	);

	onMount(async () => {
		if (!email) {
			return;
		}
		const code = searchParams?.get("code");
		if (email && code) {
			await confirmSignUp.call(email, code);
		}
	});

	async function handleConfirmSignUp(_event: SubmitEvent, form: HTMLFormElement): Promise<void> {
		if (confirmSignUp.value.status === "loading") {
			return;
		}
		if (confirmSignUp.value.status === "success") {
			const newSearchParams = new URLSearchParams(searchParams);
			newSearchParams.delete("code");
			await Auth.openAuthenticationModal(AuthenticationModalKind.SignIn, { searchParams: newSearchParams });
			return;
		}
		const formData = new FormData(form);
		const email = formData.get("email") as string;
		const code = formData.get("code") as string;
		await confirmSignUp.call(email, code);
	}
</script>

<Form class="flex w-full flex-col items-stretch gap-2 mt-5 md:mt-10" method="dialog" onsubmit={handleConfirmSignUp}>
	<Input autocomplete="email" required placeholder={translate("email")} type="email" name="email" bind:value={email} />
	<Input
		autocomplete="one-time-code"
		required
		placeholder={translate("code")}
		type="password"
		name="code"
		value={searchParams?.get("code") ?? undefined}
	/>
	<button
		class="text-sm/6 self-end py-4 mb-1.5 md:mb-4 md:text-base text-fcrgray underline"
		type="button"
		onclick={async (): Promise<void> => {
			await Auth.openAuthenticationModal(AuthenticationModalKind.ResendConfirmationCode, { email });
		}}
	>
		<Formatted text={translate(confirmSignUp.value.status === "success" ? "signInLink" : "resendCode")} />
	</button>
	<Button
		loading={confirmSignUp.value.status === "loading"}
		variant={ButtonVariant.Dark}
		size={isDesktop.current ? ButtonSize.Large : ButtonSize.Medium}
	>
		<Formatted text={translate("signInButton")} />
	</Button>

	{#if error}
		<p class="text-sm text-fcrerror mt-2 [&_a]:text-fcrbrown">
			<Formatted text={error} />
		</p>
	{:else if confirmSignUp.value.status === "success"}
		<p class="text-sm text-fcrgreen mt-2" role="none">
			<Formatted text={translate("confirmationSuccessInfo")} />
		</p>
	{/if}
</Form>
